import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { RelatedTicketInfo } from '../../../../../api/types/dasTickets.types';

interface TicketRelatedProps {
  relatedTickets?: string[]; // Legacy: array of IDs for backward compatibility
  relatedTicketDetails?: RelatedTicketInfo[]; // New: detailed related ticket info
}

// Helper to format date for display
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper to get status color
const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'approved':
      return '#10b981';
    case 'pre approved':
      return '#06b6d4';
    case 'rejected':
      return '#ef4444';
    case 'pending':
    case 'submitted':
    case 'in review':
      return '#f59e0b';
    case 'draft':
      return '#94a3b8';
    case 'cancelled':
      return '#64748b';
    default:
      return '#6b7280';
  }
};

// Legacy fallback: format UUID or request number for display
const formatTicketId = (id: string): string => {
  if (/^[A-Z]+-\d{4}-\d+$/i.test(id) || /^[A-Z]+-\d+$/i.test(id)) {
    return id;
  }
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  if (isUuid) {
    return `ID: ${id.substring(0, 8).toUpperCase()}`;
  }
  return id;
};

export const TicketRelated: React.FC<TicketRelatedProps> = ({ relatedTickets, relatedTicketDetails }) => {
  // Prefer detailed data if available
  const hasDetailedData = relatedTicketDetails && relatedTicketDetails.length > 0;
  const hasLegacyData = relatedTickets && relatedTickets.length > 0;

  if (!hasDetailedData && !hasLegacyData) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <IconSystem.link size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>No related requests found.</div>
      </div>
    );
  }

  const ticketCount = hasDetailedData ? relatedTicketDetails!.length : relatedTickets!.length;

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Related Requests ({ticketCount})
        </h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Requests that are connected to this one
        </p>
      </div>

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {hasDetailedData ? (
          // Render detailed related ticket cards
          relatedTicketDetails!.map((ticket) => (
            <div
              key={ticket.id}
              style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1rem',
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }}
            >
              {/* Header with Request Number and Status */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '0.375rem',
                      background: 'var(--primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <IconSystem.link size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {ticket.requestNumber}
                    </div>
                    {ticket.requestTypeName && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {ticket.requestTypeName}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    background: `${getStatusColor(ticket.currentStatus)}20`,
                    color: getStatusColor(ticket.currentStatus),
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {ticket.currentStatus}
                </div>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.4 }}>
                  {ticket.subject}
                </div>
              </div>

              {/* Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.75rem' }}>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Created By: </span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {ticket.createdByUserName || 'Unknown'}
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Created Date: </span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {formatDate(ticket.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          // Fallback: Render legacy ticket ID cards
          relatedTickets!.map((relatedTicketId) => (
            <div
              key={relatedTicketId}
              style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1rem',
                border: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '0.375rem',
                    background: 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <IconSystem.link size={20} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {formatTicketId(relatedTicketId)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Click to view details</div>
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                <IconSystem.chevronRight size={20} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TicketRelated;
